import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styles from './phone-confirmation-input.module.scss';
import { MiniInput, DefaultButton, Alert } from 'shared/ui';
import { NotificationApi } from 'shared/api';
import { normalizePhone } from 'shared/lib';
import type { IVkBindResponse } from 'shared/api/notification/interfaces/notification.interfaces';

export interface PhoneConfirmationInputRef {
  isVerified: boolean;
  hasVkLink: boolean;
  isVkChecked: boolean;
}

interface IPhoneConfirmationInputProps {
  onVerified?: (phone: string) => void;
  onPhoneChange?: (phone: string) => void;
}

type VkCheckState = 'idle' | 'checking' | 'linked' | 'unlinked';

export const PhoneConfirmationInput = forwardRef<
  PhoneConfirmationInputRef,
  IPhoneConfirmationInputProps
>(({ onVerified, onPhoneChange }, ref) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [viewCodeInput, setViewCodeInput] = useState(false);
  const [viewSendButton, setViewSendButton] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [vkSendsLeft, setVkSendsLeft] = useState<number | null>(null);

  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null);
  const [blockedCountdown, setBlockedCountdown] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [needsBind, setNeedsBind] = useState(false);
  const [bindData, setBindData] = useState<IVkBindResponse | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);

  const handleCopyCode = useCallback(async () => {
    if (!bindData?.bindCode) return;
    try {
      await navigator.clipboard.writeText(bindData.bindCode);
      setAlertMessage('Код скопирован в буфер обмена!');
    } catch {
      setAlertMessage('Не удалось скопировать код');
    }
  }, [bindData?.bindCode]);

  const [vkCheckState, setVkCheckState] = useState<VkCheckState>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentPhoneRef = useRef<string>('');

  useImperativeHandle(
    ref,
    () => ({
      isVerified,
      hasVkLink: vkCheckState === 'linked',
      isVkChecked: vkCheckState === 'linked' || vkCheckState === 'unlinked',
    }),
    [isVerified, vkCheckState]
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const startCountdown = useCallback((until: Date) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const diff = until.getTime() - Date.now();
      if (diff <= 0) {
        setBlockedUntil(null);
        setBlockedCountdown('');
        setError('');
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      const min = Math.floor(diff / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      setBlockedCountdown(`${min}:${String(sec).padStart(2, '0')}`);
    }, 500);
  }, []);

  const isButtonDisabled = sending || blockedUntil !== null;

  const getButtonText = () => {
    if (sending) return 'Отправка...';
    if (blockedUntil) return `Повторите через ${blockedCountdown}`;
    return 'Отправить код';
  };

  const handlePhoneChange = (value: string) => {
    const digits = value.match(/\d+/g)?.join('') ?? '';
    const normalizedPhone = normalizePhone(digits);

    setPhone(normalizedPhone);
    onPhoneChange?.(normalizedPhone);
    setCode('');
    setViewSendButton(true);
    setError('');
    setViewCodeInput(false);
    setIsVerified(false);
    setBindData(null);
    setAttemptsLeft(null);
    setVkSendsLeft(null);
    setNeedsBind(false);
    setBlockedUntil(null);
    setBlockedCountdown('');

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    currentPhoneRef.current = normalizedPhone;

    if (normalizedPhone.length >= 11) {
      setVkCheckState('checking');
      debounceRef.current = setTimeout(async () => {
        const phoneToCheck = currentPhoneRef.current;
        try {
          const result = await NotificationApi.checkVkLink(phoneToCheck);
          if (currentPhoneRef.current !== phoneToCheck) return;

          if (result.hasVkLink) {
            setVkCheckState('linked');
          } else {
            setVkCheckState('unlinked');
            setNeedsBind(true);
            try {
              const bindResult = await NotificationApi.bindVk({
                phone: phoneToCheck,
              });
              if (currentPhoneRef.current !== phoneToCheck) return;
              setBindData(bindResult);
            } catch {
              if (currentPhoneRef.current !== phoneToCheck) return;
              setError('Ошибка получения кода привязки');
            }
          }
        } catch {
          if (currentPhoneRef.current !== phoneToCheck) return;
          setVkCheckState('idle');
          setError('Не удалось проверить привязку к VK');
        }
      }, 500);
    } else {
      setVkCheckState('idle');
    }
  };

  const doSendCode = async () => {
    if (!phone || phone.length < 11) {
      setError('Введите корректный номер');
      return;
    }
    try {
      setSending(true);
      setViewSendButton(false);
      setError('');
      setBindData(null);
      setNeedsBind(false);

      const result = await NotificationApi.sendCode({ phone });
      setViewCodeInput(true);
      setAttemptsLeft(result.maxAttemptsPerRound);
      if (result.vkSendsLeft !== undefined) setVkSendsLeft(result.vkSendsLeft);
    } catch (err) {
      const parsed = NotificationApi.parseVerificationError(err);

      if (parsed?.needsBind) {
        setNeedsBind(true);
        setError('');
        try {
          const bindResult = await NotificationApi.bindVk({ phone });
          setBindData(bindResult);
        } catch {
          setError('Ошибка получения кода привязки');
        }
      } else if (parsed?.blocked && parsed?.blockedUntil) {
        setBlockedUntil(new Date(parsed.blockedUntil));
        startCountdown(new Date(parsed.blockedUntil));
        setError(parsed.message);
      } else {
        setError(
          parsed?.message || (err instanceof Error ? err.message : 'Ошибка')
        );
      }

      setViewCodeInput(false);
      setViewSendButton(true);
    } finally {
      setSending(false);
    }
  };

  const doConfirmCode = async () => {
    if (!code || code.length < 6) {
      setError('Введите код из 6 цифр');
      return;
    }
    try {
      setError('');
      await NotificationApi.confirmCode({ phone, code });
      setViewCodeInput(false);
      setIsVerified(true);
      setViewSendButton(false);
      setCode('');
      if (onVerified) {
        onVerified(phone);
      }
    } catch (err) {
      const d = NotificationApi.parseVerificationError(err);

      if (d?.attemptsLeftInRound !== undefined) {
        setAttemptsLeft(d.attemptsLeftInRound);
        setError(d.message);
        return;
      }

      if (d?.roundExhausted) {
        setError(d.message);
        setViewCodeInput(false);
        setViewSendButton(true);
        setCode('');
        return;
      }

      if (d?.blocked && d?.blockedUntil) {
        setBlockedUntil(new Date(d.blockedUntil));
        startCountdown(new Date(d.blockedUntil));
        setViewCodeInput(false);
        setViewSendButton(true);
        setError(d.message);
        return;
      }

      setError(d?.message || (err instanceof Error ? err.message : 'Ошибка'));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.container__phone_input}>
        <div className={styles.phone_input__row}>
          <div className={styles.phone_input__input}>
            <MiniInput
              isPhone={true}
              placeholder="Телефон"
              onChange={handlePhoneChange}
            />
          </div>
          {viewSendButton &&
          !isVerified &&
          !needsBind &&
          vkCheckState === 'idle' ? (
            <div className={styles.phone_input__button}>
              <DefaultButton
                buttonText={getButtonText()}
                color="green"
                onClick={doSendCode}
                disabled={isButtonDisabled}
                className={styles.compact_button}
              />
            </div>
          ) : null}
        </div>
        <div className={styles.phone_input__error}>{error}</div>
        {viewCodeInput && !isVerified ? (
          <div className={styles.phone_channel_info}>
            Код отправлен через ВКонтакте
          </div>
        ) : null}
        {attemptsLeft !== null && !isVerified ? (
          <div className={styles.phone_input__error}>
            Попыток осталось: {attemptsLeft}
            {vkSendsLeft !== null && vkSendsLeft !== undefined
              ? ` · Отправок кода осталось: ${vkSendsLeft}`
              : ''}
          </div>
        ) : null}
      </div>

      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}

      {vkCheckState === 'checking' ? (
        <div className={styles.phone_channel_info}>
          Проверка привязки к VK...
        </div>
      ) : null}

      {vkCheckState === 'linked' && !isVerified && !viewCodeInput ? (
        <div className={styles.container__vk_bind}>
          <p className={styles.bind_text}>Номер привязан к VK</p>
          <DefaultButton
            buttonText="Подтвердить"
            color="green"
            onClick={doSendCode}
            disabled={isButtonDisabled}
            className={styles.compact_button}
          />
        </div>
      ) : null}

      {needsBind && bindData?.bindCode ? (
        <div className={styles.container__vk_bind}>
          <p className={styles.bind_text}>
            Бот присылает уведомления о записях и позволяет управлять ими через
            ВК
          </p>
          <div className={styles.bind_code} onClick={handleCopyCode}>
            {bindData.bindCode}
          </div>
          <p className={styles.bind_hint}>Отправьте этот код боту ВКонтакте</p>
          <a
            href={bindData.vkBotChatUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.vk_button}
          >
            Написать боту
          </a>
          <div className={styles.bind_actions}>
            <DefaultButton
              buttonText="Код отправлен"
              color="green"
              onClick={doSendCode}
              className={styles.compact_button}
            />
            <DefaultButton
              buttonText="Обновить код"
              color="green"
              onClick={async () => {
                const result = await NotificationApi.bindVk({ phone });
                setBindData(result);
              }}
              className={styles.compact_button}
            />
          </div>
        </div>
      ) : null}

      {vkCheckState === 'unlinked' && !bindData ? (
        <div className={styles.phone_channel_info}>
          Загрузка кода привязки...
        </div>
      ) : null}

      {viewCodeInput ? (
        <div className={styles.container__confirm_input}>
          <div className={styles.confirm_input__row}>
            <div className={styles.confirm_input__input}>
              <MiniInput
                inputValue={code}
                placeholder="Код"
                onChange={(v) => setCode(v)}
              />
            </div>
            <div className={styles.confirm_input__button}>
              <DefaultButton
                buttonText="Подтвердить"
                color="green"
                onClick={doConfirmCode}
                className={styles.compact_button}
              />
            </div>
          </div>
        </div>
      ) : null}

      {isVerified ? (
        <div className={styles.verified_text}>✓ Номер подтверждён</div>
      ) : null}
    </div>
  );
});
